// src/services/relationshipService.js
import Cookies from "js-cookie";
import axios from "axios";
import { axiosRelationshipInstance } from "../AxiosConfiguration";
import { showErrorToast } from "../Utils/toast";

const relationshipUrl = process.env.REACT_APP_BASE_URL_ASSESSMENT;

// Lookup whether a user exists on Aris by email or phone
// POST /api/v2/relationship/user-lookup
// payload: { email } OR { country_code, contact }
// response: { status, status_code, message, payload: { exists: boolean, user?: { first_name, last_name, profile_pic_url } } }
export const userLookup = async ({ email, country_code, contact }) => {
  try {
    const body = {};
    if (email) body.email = email;
    if (country_code && contact) {
      body.country_code = country_code;
      body.contact = contact;
    }
    const res = await axiosRelationshipInstance.post(
      "/api/v2/relationship/user-lookup",
      body
    );
    const raw = res?.data || {};
    const exists = Boolean(raw?.payload?.exists ?? raw?.exists);
    const user = raw?.payload?.user || raw?.user || null;
    return { exists, user, error: null };
  } catch (error) {
    // Handle 403 plan validation error specifically - don't show toast, return error details
    if (error?.response?.status === 403) {
      const message = error?.response?.data?.message || "Relationship requests can only be sent to users on Aligned or Aligned+ plans";
      return { exists: false, user: null, error: { status: 403, message } };
    }
    // For other errors, show toast and return safe default
    const apiMessage = error?.response?.data?.message || error?.message || "Something went wrong";
    showErrorToast(apiMessage);
    return { exists: false, user: null, error: { status: error?.response?.status, message: apiMessage } };
  }
};


export const addRelationship = async (formData) => {
  try {
    const idToken = Cookies.get("idtoken"); // Retrieve the idToken from cookies

    const requestData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      nickname: formData.nickname || null, // Include optional fields with defaults
      relationship_type: formData.relationship_type,
      relationship_length: formData.relationship_length,
      relationship_category: formData.relationship_category,
      relationship_status: formData.relationship_status,
      relationship_goals: formData.relationship_goals || null, // Optional goals field
      email: formData.email || null, // Optional email
      country_code: formData.country_code || null,
      contact: formData.contact || null, // Optional contact
      flow: formData.flow || null,
    };

    // v2: Create relationship via app
    // POST /api/v2/relationship/app
    const response = await axiosRelationshipInstance.post(
      "/api/v2/relationship/add",
      requestData,
      {
        headers: {
          Authorization: `Bearer ${idToken}`, // Include the idToken in headers
        },
      }
    );

    // v2 returns { message, data } OR { status, status_code, payload }
    // Normalize to legacy shape expected by callers: { status: 'success', payload, message, status_code }
    const raw = response?.data || {};
    const normalizedPayload = raw?.data || raw?.payload || raw || null;
    return {
      status: "success",
      status_code: raw?.status_code,
      payload: normalizedPayload,
      message: raw?.message || "relationship_added",
    };
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error adding relationship";
    showErrorToast(apiMessage);
    return {
      status: "error",
      status_code: error?.response?.status,
      payload: null,
      message: apiMessage,
    };
  }
};

export const fetchCategoriesAndTypes = async (field = "") => {
  if (field && field === 'status') {
    return [
      "Fair",
      "Medium",
      "Strong"
    ]
  }

  const category_and_types = {
    "Dating": [
      "Boyfriend",
      "Girlfriend",
      "Partner"
    ],
    "Marriage": [
      "Husband",
      "Wife",
      "Partner"
    ],
    "Family": [
      "Father",
      "Mother",
      "Brother",
      "Sister",
      "Son",
      "Daughter",
      "Cousin",
      "Grandfather",
      "Grandmother",
      "Mother-in-Law",
      "Father-in-Law",
      "Son-in-Law",
      "Daughter-in-Law",
      "Sister-in-Law",
      "Brother-in-Law",
      "Uncle",
      "Aunt",
      "Grandson",
      "Granddaughter",
      "Extended In-Laws",
      "Half-Sibling",
      "Step-Relations",
      "Other"
    ],
    "Friends": [
      "Friend",
      "Close Friends",
      "Best Friend",
      "Childhood Friend",
      "Grammar School Friend",
      "High School Friend",
      "College Friend",
      "Virtual Friend",
      "Work Friend",
      "Other"
    ],
    "Workplace": [
      "Business Partner",
      "Investor",
      "Manager",
      "Colleague",
      "Supervisor",
      "Freelancer/Contractor",
      "Peer",
      "Direct Report",
      "Client",
      "Mentor",
      "Mentee",
      "Other"
    ],
    "Roommates": [
      "College Roommate",
      "Work Colleague Roommate",
      "Long-term Partner Roommate",
      "Short-term Roommate",
      "Other"
    ],
    "Sports": [
      "Coach",
      "Assistant Coach",
      "Teammate",
      "Athlete/Player",
      "Trainer",
      "Agent",
      "Referee/Umpire",
      "Sponsor",
      "Other"
    ],
    "Others": [
      "Mentor",
      "Executive Coach",
      "Doctor",
      "Student",
      "Professor/Teacher",
      "Therapist/Counselor",
      "Advisor",
      "Legal Counsel",
      "Caregiver",
      "Accountant",
      "Trainer",
      "Neighbor",
      "Priest/Pastor",
      "Rabbi",
      "Other"
    ]

  }
  if (field) {
    return category_and_types[field]
  }
  return Object.keys(category_and_types)
};

export const getRelationships = async (category = "") => {
  try {
    // v2: /api/v2/relationship/my can return { message, data: [...] } or { status, payload: [...] }
    const response = await axiosRelationshipInstance.get(
      `/api/v2/relationship/my`,
      {
        params: category ? { category } : {},
      }
    );
    const raw = response?.data || {};
    const list = raw?.data || raw?.payload || [];
    return Array.isArray(list) ? list : [];
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error fetching relationships";
    showErrorToast(apiMessage);
    return [];
  }
};

// fetchPendingRequests removed: no longer used

export const acceptRequest = async (relationId, relationshipData) => {
  try {
    // v2: POST /api/v2/relationship/{relation_id}/accept
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${relationId}/accept`,
      relationshipData
    );
    return response.data; // Return the response data for further use
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error accepting request";
    showErrorToast(apiMessage);
    return null;
  }
};

// Deny relationship request
export const denyRequest = async (relationId) => {
  try {
    // v2: POST /api/v2/relationship/{relation_id}/decline
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${relationId}/decline`
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error denying request";
    showErrorToast(apiMessage);
    return null;
  }
};

// Fetch a single relationship request by request_id
export const getRequestById = async (requestId) => {
  try {
    const res = await axiosRelationshipInstance.get(
      `/api/v2/relationship/request/${requestId}`
    );
    // API returns { status, status_code, message, payload }
    return res?.data?.payload || null;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to fetch request";
    showErrorToast(apiMessage);
    return null;
  }
};

// Accept a request by request_id (new API)
export const acceptRequestByRequestId = async (requestId, body) => {
  try {
    const res = await axiosRelationshipInstance.post(
      `/api/v2/relationship/request/${requestId}/accept`,
      body || {}
    );
    return res?.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to accept request";
    showErrorToast(apiMessage);
    return null;
  }
};

// Decline a request by request_id (new API)
export const declineRequestByRequestId = async (requestId) => {
  try {
    const res = await axiosRelationshipInstance.post(
      `/api/v2/relationship/request/${requestId}/decline`
    );
    return res?.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to decline request";
    showErrorToast(apiMessage);
    return null;
  }
};

// Fetch sent requests for a specific relation (used to prefill share contact for INVITED relationships)
export const getSentRequestsByRelationId = async (relationId) => {
  try {
    const res = await axiosRelationshipInstance.get(
      `/api/v2/relationship/${relationId}/request/sent`
    );
    // Expecting { status, status_code, message, payload: [...] }
    return res?.data?.payload || {};
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to fetch sent requests";
    showErrorToast(apiMessage);
    return [];
  }
};

// Attach invite to an existing relationship (when accepting as existing)
export const attachInvite = async (inviteRelationId, body) => {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${inviteRelationId}/attach-invite`,
      body || {}
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error attaching invite";
    showErrorToast(apiMessage);
    return null;
  }
};

export const editRelationship = async (relationshipId, relationshipData) => {
  try {
    // v2: PUT /api/v2/relationship/{relation_id}
    const response = await axiosRelationshipInstance.put(
      `/api/v2/relationship/${relationshipId}`,
      relationshipData
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to edit relationship";
    showErrorToast(apiMessage);
    return null;
  }
};

export const deleteRelationship = async (relationshipId) => {
  try {
    // v2: DELETE /api/v2/relationship/{relation_id}
    // This endpoint now handles:
    // - Relationship deletion (sets status to DELETED)
    // - Disconnection (sets counterpart to BLOCKED)
    // - Unsharing all shared elements
    // - Notifying the counterpart
    const response = await axiosRelationshipInstance.delete(
      `/api/v2/relationship/${relationshipId}`
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to delete relationship";
    showErrorToast(apiMessage);
    return null;
  }
};

export const deleteConnection = async (relationshipId) => {
  try {
    const response = await axiosRelationshipInstance.delete(
      `/api/v2/relationship/${relationshipId}/connection` // Path without query string
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to delete relationship:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export async function fetchInsightContent(
  field,
  relation_id,
  model,
  u1_status,
  u2_status,
  history
) {
  try {
    // Direct axios GET request
    const response = await axiosRelationshipInstance.post(
      `/api/relation/adviceinsight`,
      {
        relation_id: relation_id,
        field: field,
        model: model,
        u1_status: u1_status,
        u2_status: u2_status,
        history: history,
      }
    );

    // Axios already parses JSON, so you can directly return the data
    return response.data.payload;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error fetching insight content";
    // showErrorToast(apiMessage);
    return "Failed to fetch content.";
  }
}

export async function fetchInsightMetrics(relation_id) {
  try {
    // Direct axios GET request
    const response = await axiosRelationshipInstance.get(
      `/api/relation/relation/get_metrics?relationship_id=${relation_id}`
    );
    // Axios already parses JSON, so you can directly return the data
    return response.data.payload;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error fetching insight content";
    // showErrorToast(apiMessage);
    return "Failed to fetch content.";
  }
}

//
export async function sendInviteWithEmailRelationID(relation_id, email) {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${relation_id}/share-elements`,
      {
        email,
      }
    );
    // Axios already parses JSON, so you can directly return the data
    return response.data;
  } catch (error) {
    // Handle 403 plan validation error specifically
    if (error?.response?.status === 403) {
      const description = error?.response?.data?.detail?.description;
      if (description) {
        showErrorToast(description);
      } else {
        showErrorToast("This user must be on an Aligned or Aligned+ plan to receive relationship requests");
      }
    } else {
      const apiMessage = error?.response?.data?.detail?.description || error?.response?.data?.message || error?.message || "Failed to share elements";
      showErrorToast(apiMessage);
    }
    return null;
  }
}
//
export async function updateConnectionEmail(relation_id, email) {
  try {
    const response = await axiosRelationshipInstance.put(
      `/api/v2/relationship/${relation_id}/email`,
      {
        email: email,
      }
    );
    // Axios already parses JSON, so you can directly return the data
    return response.data;
  } catch (error) {
    console.error("Error fetching insight content:", error);
    // Provide a fallback content
    throw error;
  }
}

// Helper function to convert a blob URL to a File object
async function blobUrlToFile(blobUrl, filename, mimeType) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
}

export const uploadURL = async (relation_id, data) => {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${relation_id}/upload-url`,
      data
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Failed to create upload URL";
    showErrorToast(apiMessage);
    return null;
  }
};

export const uploadFiles = async (
  relationId,
  imageKey = null,
  audioKey = null
) => {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${relationId}/upload-files?relation_id=${relationId}&image=${imageKey}&mp3=${audioKey}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchOnboardingData = async (step, formData = {}) => {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/static/onboarding/${step}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error fetching data";
    showErrorToast(apiMessage);
    return null;
  }
};

export const getsentrequests = async () => {
  try {
    // v2: List connection requests sent
    // Note: Assuming corrected path '/api/v2/relationship/requests/sent'
    const response = await axiosRelationshipInstance.get(
      `/api/v2/relationship/requests/sent`
    );
    return response?.data?.payload || [];
  } catch (error) {
    const apiMessage = error?.response?.data?.message || error?.message || "Error fetching user profile";
    showErrorToast(apiMessage);
    return null;
  }
};

export const getRelationshipProfile = async (relationship_id) => {
  try {
    const response = await axiosRelationshipInstance.get(
      `/api/v2/relationship/${relationship_id}/profile`,
      {
        headers: {
          Authorization: `Bearer ${Cookies.get("idtoken")}`,
        },
      }
    );
    if (response.data && response.data.status === "success") {
      return response.data.payload; // Return the payload from the response
    } else {
      throw new Error(response.data.message || "Unexpected response format");
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};


export const getRelationshipAdvice = async (category, type) => {
  try {
    const advice_model = {
      "Dating": {
        "Boyfriend": [
          "Build Trust",
          "Communicate Effectively",
          "Manage Expectations",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Set Boundaries",
          "Balance Independence and Togetherness",
          "Express Affection",
          "Navigate Commitment"
        ],
        "Girlfriend": [
          "Build Trust",
          "Communicate Effectively",
          "Manage Expectations",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Set Boundaries",
          "Balance Independence and Togetherness",
          "Express Affection",
          "Navigate Commitment"
        ],
        "Partner": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Manage Finances Together",
          "Support Life Changes",
          "Maintain Intimacy",
          "Share Responsibilities",
          "Nurture Long-term Connection"
        ]
      },
      "Marriage": {
        "Husband": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Manage Finances Together",
          "Support Life Changes",
          "Maintain Intimacy",
          "Share Responsibilities",
          "Nurture Long-term Connection"
        ],
        "Wife": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Manage Finances Together",
          "Support Life Changes",
          "Maintain Intimacy",
          "Share Responsibilities",
          "Nurture Long-term Connection"
        ],
        "Partner": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Apologize",
          "Manage Finances Together",
          "Support Life Changes",
          "Maintain Intimacy",
          "Share Responsibilities",
          "Nurture Long-term Connection"
        ]
      },
      "Family": {
        "Father": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Seek and Give Support",
          "Respect Parental Boundaries",
          "Navigate Life Changes",
          "Apologize",
          "Strengthen Family Bonds"
        ],
        "Mother": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Seek and Give Support",
          "Respect Parental Boundaries",
          "Navigate Life Changes",
          "Apologize",
          "Strengthen Family Bonds"
        ],
        "Brother": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Each Other",
          "Share Responsibilities",
          "Respect Boundaries",
          "Maintain Family Bonds",
          "Celebrate Successes Together",
          "Navigate Childhood Rivalry"
        ],
        "Sister": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Each Other",
          "Share Responsibilities",
          "Respect Boundaries",
          "Maintain Family Bonds",
          "Celebrate Successes Together",
          "Navigate Childhood Rivalry"
        ],
        "Son": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Guidance",
          "Encourage Independence",
          "Foster Emotional Intelligence",
          "Resolve Conflict",
          "Set and Enforce Boundaries",
          "Motivate",
          "Teach Responsibility",
          "Navigate Transitions"
        ],
        "Daughter": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Guidance",
          "Encourage Independence",
          "Foster Emotional Intelligence",
          "Resolve Conflict",
          "Set and Enforce Boundaries",
          "Motivate",
          "Teach Responsibility",
          "Navigate Transitions"
        ],
        "Father-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Seek and Give Support",
          "Respect Parental Boundaries",
          "Navigate Life Changes",
          "Apologize",
          "Strengthen Family Bonds"
        ],
        "Mother-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Seek and Give Support",
          "Respect Parental Boundaries",
          "Navigate Life Changes",
          "Apologize",
          "Strengthen Family Bonds"
        ],
        "Son-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Guidance",
          "Encourage Independence",
          "Foster Emotional Intelligence",
          "Resolve Conflict",
          "Set and Enforce Boundaries",
          "Motivate",
          "Teach Responsibility",
          "Navigate Transitions"
        ],
        "Daughter-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Guidance",
          "Encourage Independence",
          "Foster Emotional Intelligence",
          "Resolve Conflict",
          "Set and Enforce Boundaries",
          "Motivate",
          "Teach Responsibility",
          "Navigate Transitions"
        ],
        "Sister-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Each Other",
          "Share Responsibilities",
          "Respect Boundaries",
          "Maintain Family Bonds",
          "Celebrate Successes Together",
          "Navigate Childhood Rivalry"
        ],
        "Brother-in-Law": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Each Other",
          "Share Responsibilities",
          "Respect Boundaries",
          "Maintain Family Bonds",
          "Celebrate Successes Together",
          "Navigate Childhood Rivalry"
        ],
        "Grandfather": [
          "Build Trust",
          "Communicate Effectively",
          "Share Wisdom and Experience",
          "Provide Emotional Support",
          "Respect Boundaries",
          "Nurture Family Traditions",
          "Foster Intergenerational Connection",
          "Resolve Family Conflicts",
          "Offer Guidance with Sensitivity",
          "Support Changing Family Dynamics"
        ],
        "Grandmother": [
          "Build Trust",
          "Communicate Effectively",
          "Share Wisdom and Experience",
          "Provide Emotional Support",
          "Respect Boundaries",
          "Nurture Family Traditions",
          "Foster Intergenerational Connection",
          "Resolve Family Conflicts",
          "Offer Guidance with Sensitivity",
          "Support Changing Family Dynamics"
        ],
        "Uncle": [
          "Build Trust",
          "Communicate Effectively",
          "Respect Individual Differences",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Through Challenges",
          "Share Responsibilities",
          "Strengthen Family Bonds",
          "Handle Family Traditions",
          "Balance Family and Personal Needs"
        ],
        "Aunt": [
          "Build Trust",
          "Communicate Effectively",
          "Respect Individual Differences",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Through Challenges",
          "Share Responsibilities",
          "Strengthen Family Bonds",
          "Handle Family Traditions",
          "Balance Family and Personal Needs"
        ],
        "Grandson": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation and Respect",
          "Maintain Regular Contact",
          "Share Life Updates",
          "Seek Advice and Wisdom",
          "Involve Them in Family Events",
          "Provide Emotional Support",
          "Respect Their Autonomy",
          "Assist with Technology and Modern Challenges"
        ],
        "Granddaughter": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation and Respect",
          "Maintain Regular Contact",
          "Share Life Updates",
          "Seek Advice and Wisdom",
          "Involve Them in Family Events",
          "Provide Emotional Support",
          "Respect Their Autonomy",
          "Assist with Technology and Modern Challenges"
        ],
        "Extended In-Laws": [
          "Build Trust",
          "Communicate Effectively",
          "Respect Individual Differences",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Through Challenges",
          "Share Responsibilities",
          "Strengthen Family Bonds",
          "Handle Family Traditions",
          "Balance Family and Personal Needs"
        ],
        "Half-Sibling": [
          "Build Trust",
          "Communicate Effectively",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Each Other",
          "Share Responsibilities",
          "Respect Boundaries",
          "Maintain Family Bonds",
          "Celebrate Successes Together",
          "Navigate Childhood Rivalry"
        ],
        "Step-Relations": [
          "Build Trust",
          "Communicate Effectively",
          "Respect Individual Differences",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Support Through Challenges",
          "Share Responsibilities",
          "Strengthen Family Bonds",
          "Handle Family Traditions",
          "Balance Family and Personal Needs"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ]
      },
      "Friends": {
        "Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Best Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Childhood Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Grammar School Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "High School Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "College Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Virtual Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Work Friend": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ],
        "Close Friends": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ]
      },
      "Workplace": {
        "Business Partner": [
          "Build Trust",
          "Communicate Effectively",
          "Collaborate on Strategic Decisions",
          "Resolve Conflict",
          "Align on Vision and Goals",
          "Share Leadership Roles",
          "Handle Financial Management",
          "Navigate Power Dynamics",
          "Provide Constructive Feedback",
          "Plan for Long-Term Success"
        ],
        "Investor": [
          "Build Trust",
          "Communicate Vision and Goals",
          "Provide Transparent Updates",
          "Seek Strategic Input",
          "Manage Financial Expectations",
          "Collaborate on Growth Strategies",
          "Address Investor Concerns",
          "Share Successes and Challenges",
          "Demonstrate Accountability",
          "Foster Long-term Partnership"
        ],
        "Manager": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Upward Feedback",
          "Present Ideas Clearly",
          "Manage Expectations",
          "Resolve Conflict",
          "Demonstrate Initiative",
          "Align with Team Goals",
          "Balance Workload Effectively",
          "Foster a Collaborative Environment"
        ],
        "Colleague": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ],
        "Supervisor": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Upward Feedback",
          "Present Ideas Clearly",
          "Manage Expectations",
          "Resolve Conflict",
          "Demonstrate Initiative",
          "Align with Team Goals",
          "Balance Workload Effectively",
          "Foster a Collaborative Environment"
        ],
        "Freelancer/Contractor": [
          "Build Trust",
          "Communicate Effectively",
          "Collaborate on Strategic Decisions",
          "Resolve Conflict",
          "Align on Vision and Goals",
          "Share Leadership Roles",
          "Handle Financial Management",
          "Navigate Power Dynamics",
          "Provide Constructive Feedback",
          "Plan for Long-Term Success"
        ],
        "Peer": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ],
        "Direct Report": [
          "Build Trust",
          "Communicate Effectively",
          "Motivate and Inspire",
          "Provide Constructive Feedback",
          "Develop Talent",
          "Resolve Conflict",
          "Delegate Effectively",
          "Foster a Positive Work Environment",
          "Set Clear Expectations",
          "Recognize and Reward Achievement"
        ],
        "Client": [
          "Build Trust",
          "Communicate Effectively",
          "Collaborate on Strategic Decisions",
          "Resolve Conflict",
          "Align on Vision and Goals",
          "Share Leadership Roles",
          "Handle Financial Management",
          "Navigate Power Dynamics",
          "Provide Constructive Feedback",
          "Plan for Long-Term Success"
        ],
        "Mentor": [
          "Build Trust",
          "Communicate Openly",
          "Ask for Feedback",
          "Show Appreciation",
          "Share Your Goals",
          "Seek Guidance",
          "Demonstrate Willingness to Learn",
          "Respect Mentor’s Time",
          "Provide Updates on Progress",
          "Be Open to Challenges"
        ],
        "Mentee": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Guidance and Feedback",
          "Motivate and Encourage",
          "Share Experience and Knowledge",
          "Help Set and Achieve Goals",
          "Foster Growth and Development",
          "Resolve Mentee Challenges",
          "Build Confidence",
          "Maintain Boundaries and Professionalism"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ]
      },
      "Sports": {
        "Coach": [
          "Build Trust",
          "Communicate Goals Clearly",
          "Provide Honest Feedback",
          "Be Open to New Perspectives",
          "Demonstrate Commitment",
          "Share Challenges and Successes",
          "Respect the Coaching Process",
          "Seek Guidance on Leadership Growth",
          "Reflect on Insights",
          "Collaborate on Action Plans"
        ],
        "Assistant Coach": [
          "Motivate",
          "Communicate Effectively",
          "Build Trust",
          "Provide Targeted Feedback",
          "Resolve Team Conflicts",
          "Promote Teamwork",
          "Develop Mental Toughness",
          "Encourage Sportsmanship",
          "Set Goals",
          "Handle Performance Pressure"
        ],
        "Teammate": [
          "Collaborate",
          "Communicate Effectively",
          "Build Trust",
          "Motivate Each Other",
          "Resolve Conflict",
          "Foster Team Unity",
          "Share Leadership",
          "Support During Losses",
          "Manage Competition",
          "Encourage Fair Play"
        ],
        "Athlete/Player": [
          "Communicate Effectively",
          "Provide Feedback",
          "Build Trust",
          "Motivate",
          "Develop Discipline",
          "Support Personal Growth",
          "Foster Team Collaboration",
          "Address Individual Needs",
          "Set and Achieve Goals",
          "Manage Stress and Performance Anxiety"
        ],
        "Trainer": [
          "Motivate",
          "Communicate Effectively",
          "Build Trust",
          "Provide Targeted Feedback",
          "Resolve Team Conflicts",
          "Promote Teamwork",
          "Develop Mental Toughness",
          "Encourage Sportsmanship",
          "Set Goals",
          "Handle Performance Pressure"
        ],
        "Agent": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Upward Feedback",
          "Present Ideas Clearly",
          "Manage Expectations",
          "Resolve Conflict",
          "Demonstrate Initiative",
          "Align with Team Goals",
          "Balance Workload Effectively",
          "Foster a Collaborative Environment"
        ],
        "Referee/Umpire": [
          "Build Trust",
          "Communicate Goals Clearly",
          "Provide Honest Feedback",
          "Be Open to New Perspectives",
          "Demonstrate Commitment",
          "Share Challenges and Successes",
          "Respect the Coaching Process",
          "Seek Guidance on Leadership Growth",
          "Reflect on Insights",
          "Collaborate on Action Plans"
        ],
        "Sponsor": [
          "Build Trust",
          "Communicate Effectively",
          "Collaborate on Strategic Decisions",
          "Resolve Conflict",
          "Align on Vision and Goals",
          "Share Leadership Roles",
          "Handle Financial Management",
          "Navigate Power Dynamics",
          "Provide Constructive Feedback",
          "Plan for Long-Term Success"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ]
      },
      "Roommates": {
        "College Roommate": [
          "Communicate Effectively",
          "Collaborate on Shared Spaces",
          "Build Trust",
          "Resolve Conflict",
          "Respect Privacy",
          "Set House Rules",
          "Share Responsibilities Fairly",
          "Handle Financial Matters",
          "Disagree with Empathy",
          "Apologize"
        ],
        "Work Colleague Roommate": [
          "Communicate Effectively",
          "Collaborate on Shared Spaces",
          "Build Trust",
          "Resolve Conflict",
          "Respect Privacy",
          "Set House Rules",
          "Share Responsibilities Fairly",
          "Handle Financial Matters",
          "Disagree with Empathy",
          "Apologize"
        ],
        "Long-term Partner Roommate": [
          "Communicate Effectively",
          "Collaborate on Shared Spaces",
          "Build Trust",
          "Resolve Conflict",
          "Respect Privacy",
          "Set House Rules",
          "Share Responsibilities Fairly",
          "Handle Financial Matters",
          "Disagree with Empathy",
          "Apologize"
        ],
        "Short-term Roommate": [
          "Communicate Effectively",
          "Collaborate on Shared Spaces",
          "Build Trust",
          "Resolve Conflict",
          "Respect Privacy",
          "Set House Rules",
          "Share Responsibilities Fairly",
          "Handle Financial Matters",
          "Disagree with Empathy",
          "Apologize"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics"
        ]
      },
      "Others": {
        "Executive Coach": [
          "Build Trust",
          "Communicate Goals Clearly",
          "Provide Honest Feedback",
          "Be Open to New Perspectives",
          "Demonstrate Commitment",
          "Share Challenges and Successes",
          "Respect the Coaching Process",
          "Seek Guidance on Leadership Growth",
          "Reflect on Insights",
          "Collaborate on Action Plans"
        ],
        "Doctor": [
          "Build Trust",
          "Communicate Symptoms and Concerns Clearly",
          "Follow Medical Advice",
          "Ask Questions and Clarifications",
          "Share Complete Medical History",
          "Provide Feedback on Treatment",
          "Respect Doctor’s Expertise",
          "Stay Engaged in Health Decisions",
          "Adhere to Treatment Plans",
          "Be Honest About Challenges and Symptoms"
        ],
        "Student": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Constructive Feedback",
          "Motivate",
          "Manage Classroom Dynamics",
          "Resolve Conflicts",
          "Encourage Active Participation",
          "Foster Critical Thinking",
          "Support Personal Growth",
          "Celebrate Achievements"
        ],
        "Teacher": [
          "Build Trust",
          "Communicate Effectively",
          "Seek Feedback",
          "Demonstrate Initiative",
          "Engage in Classroom Activities",
          "Resolve Conflicts",
          "Show Appreciation",
          "Align on Academic Goals",
          "Collaborate on Learning Plans",
          "Respect Classroom Dynamics"
        ],
        "Patient": [
          "Build Trust",
          "Communicate Effectively",
          "Provide Clear Information",
          "Show Empathy and Compassion",
          "Resolve Concerns",
          "Respect Patient Autonomy",
          "Encourage Treatment Adherence",
          "Manage Expectations",
          "Navigate Difficult Conversations",
          "Support Mental and Emotional Well-being"
        ],
        "Therapist/Counselor": [
          "Build Trust",
          "Communicate Symptoms and Concerns Clearly",
          "Follow Medical Advice",
          "Ask Questions and Clarifications",
          "Share Complete Medical History",
          "Provide Feedback on Treatment",
          "Respect Doctor’s Expertise",
          "Stay Engaged in Health Decisions",
          "Adhere to Treatment Plans",
          "Be Honest About Challenges and Symptoms"
        ],
        "Advisor": [
          "Build Trust",
          "Communicate Needs and Goals",
          "Provide Clear Information",
          "Be Open to Feedback",
          "Respect Advisor’s Expertise",
          "Seek Strategic Guidance",
          "Collaborate on Solutions",
          "Share Progress and Challenges",
          "Ask Questions",
          "Value the Advisor-Client Relationship"
        ],
        "Legal Counsel": [
          "Build Trust",
          "Communicate Needs and Concerns",
          "Provide Complete and Accurate Information",
          "Follow Legal Advice",
          "Manage Expectations",
          "Be Open to Legal Strategies",
          "Respect Attorney’s Expertise",
          "Stay Engaged in Legal Process",
          "Provide Feedback on Representation",
          "Maintain Regular Communication"
        ],
        "Accountant": [
          "Build Trust",
          "Communicate Legal Options Clearly",
          "Provide Expert Legal Advice",
          "Navigate Complex Legal Issues",
          "Manage Client Expectations",
          "Maintain Confidentiality",
          "Advocate for Client’s Best Interests",
          "Resolve Legal Disputes",
          "Ensure Ethical Compliance",
          "Offer Continuous Legal Support"
        ],
        "Caregiver": [
          "Build Trust",
          "Communicate Effectively",
          "Show Appreciation",
          "Resolve Conflict",
          "Disagree with Empathy",
          "Seek and Give Support",
          "Respect Parental Boundaries",
          "Navigate Life Changes",
          "Apologize",
          "Strengthen Family Bonds"
        ],
        "Priest/Pastor": [
          "Build Trust",
          "Communicate Goals Clearly",
          "Provide Honest Feedback",
          "Be Open to New Perspectives",
          "Demonstrate Commitment",
          "Share Challenges and Successes",
          "Respect the Coaching Process",
          "Seek Guidance on Leadership Growth",
          "Reflect on Insights",
          "Collaborate on Action Plans"
        ],
        "Rabbi": [
          "Build Trust",
          "Communicate Goals Clearly",
          "Provide Honest Feedback",
          "Be Open to New Perspectives",
          "Demonstrate Commitment",
          "Share Challenges and Successes",
          "Respect the Coaching Process",
          "Seek Guidance on Leadership Growth",
          "Reflect on Insights",
          "Collaborate on Action Plans"
        ],
        "Neighbor": [
          "Build Trust",
          "Communicate Effectively",
          "Be There in Tough Times",
          "Disagree with Empathy",
          "Resolve Conflict",
          "Support Growth and Change",
          "Navigate Jealousy and Comparison",
          "Celebrate Successes Together",
          "Apologize",
          "Maintain Mutual Interests"
        ],
        "Other": [
          "Communicate Effectively",
          "Collaborate",
          "Build Trust",
          "Provide Constructive Feedback",
          "Manage Professional Boundaries",
          "Resolve Conflict",
          "Disagree with Respect",
          "Foster Team Spirit",
          "Share Credit and Success",
          "Handle Workplace Dynamics\""
        ]
      }
    }
    return {"advice": advice_model[category][type]}
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const getRelationshipInsightModel = async () => {
  try {
    const response = await axiosRelationshipInstance.get(
      `/api/relation/get_insights`
    );
    if (response.data && response.data.status === "success") {
      return response.data.payload; // Return the payload from the response
    } else {
      throw new Error(response.data.message || "Unexpected response format");
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const sendJoinInvite = async (payload) => {
  // Accepts: { relation_id, email?, country_code?, contact?, resend_nonexist? }
  try {
    const body = {
      email: payload.email || payload.primary_email || undefined,
      country_code: payload.country_code || undefined,
      contact: payload.contact || undefined,
    };
    const response = await axiosRelationshipInstance.post(
      `/api/v2/relationship/${payload.relation_id}/share-elements`,
      body
    );
    if (response.data && response.data.status === "success") {
      return response.data;
    } else {
      throw new Error(response.data.message || "Unexpected response format");
    }
  } catch (error) {
    console.error(
      "Failed to send invitation:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const resendInvite = async (relation_id) => {
  try {
    const response = await axiosRelationshipInstance.post(
      `/api/relation/resend_invite/${relation_id}`
    );
    if (response.data && response.data.status === "success") {
      return response.data;
    } else {
      throw new Error(response.data.message || "Unexpected response format");
    }
  } catch (error) {
    console.error("Error sending invitation:", error);
    throw error;
  }
};
